/* eslint-disable react/prop-types */
/**
 * Iter 290zp.12 (Stage B) — Proposal-only attachments uploader.
 *
 * Renders inside the Report Builder card. Lists every attachment in
 * `workbench_report_attachments` for the current draft and exposes:
 *   • A drop / file-pick affordance (PDFs + images, multi-select).
 *   • Per-row inclusion select (Appendix / Inline-in-body), section
 *     label, optional rep caption.
 *   • Delete.
 *
 * These rows live OUTSIDE the brainstorm chat — purely for files the
 * rep wants in the final customer PDF (IQ Retail quotes, vendor
 * datasheets, drawings, certifications) without polluting the chat
 * history. The backend merges them with chat-promoted attachments at
 * PDF compose time.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText, Image as ImageIcon, Loader2, Plus, Trash2,
  FileStack, FileImage, AlertTriangle, ChevronUp, ChevronDown,
} from "lucide-react";
import api from "../../lib/api";
import { uploadFile } from "../../lib/uploads";
import { toast } from "../ui/sonner";

const INCLUSION_OPTIONS = [
  { value: "appendix", label: "Appendix" },
  { value: "inline", label: "Inline in body" },
];

function _kindFromMeta(filename, contentType) {
  const ct = (contentType || "").toLowerCase();
  const fn = (filename || "").toLowerCase();
  if (ct === "application/pdf" || fn.endsWith(".pdf")) return "pdf";
  if (ct.startsWith("image/")) return "image";
  return "file";
}

function AttachmentRow({ row, onPatch, onDelete, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const kind = _kindFromMeta(row.filename, row.content_type);
  const isPdf = kind === "pdf";
  const Icon = isPdf ? FileText : ImageIcon;
  const tint = isPdf ? "text-rose-300" : "text-cyan-300";
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const isAppendix = row.report_inclusion === "appendix";
  // Iter 427.p7 — chat-sourced rows are read-only here; the operator
  // manages inclusion/delete from the chat message controls.
  const isChatSourced = row.source === "chat";

  const patch = async (updates) => {
    setSaving(true);
    try {
      await onPatch(row.id, updates);
    } finally {
      setSaving(false);
    }
  };

  // PDFs always render as-is — Inline option is hidden.
  const opts = isPdf
    ? INCLUSION_OPTIONS.filter((o) => o.value !== "inline")
    : INCLUSION_OPTIONS;

  return (
    <div
      className="rounded border border-white/10 bg-white/5 px-2 py-1.5 space-y-1"
      data-testid={`proposal-att-row-${row.id}`}
      data-inclusion={row.report_inclusion}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`w-3.5 h-3.5 shrink-0 ${tint}`} />
        <span
          className="text-xs text-[#e5e7eb] truncate flex-1 min-w-0"
          title={row.filename || ""}
        >
          {row.filename || "Untitled"}
        </span>
        {isChatSourced && (
          <span
            className="shrink-0 text-[9px] uppercase tracking-wider text-cyan-200/70 border border-cyan-400/25 rounded px-1 py-[1px]"
            title="Attached from the brainstorm chat and marked as appendix. Manage from the chat controls."
            data-testid={`proposal-att-chat-chip-${row.id}`}
          >
            from chat
          </span>
        )}
        {/* Iter 427.p7 — Dirk: "We need a way to arrange appendices."
            Chevron pair only rendered for appendix rows (inline images
            are placed by section heading, not by order). */}
        {isAppendix && (
          <span className="inline-flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => onMoveUp?.(row.id)}
              disabled={!canMoveUp}
              className="p-0.5 text-[#647087] hover:text-cyan-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move up in the appendix order"
              data-testid={`proposal-att-move-up-${row.id}`}
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onMoveDown?.(row.id)}
              disabled={!canMoveDown}
              className="p-0.5 text-[#647087] hover:text-cyan-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move down in the appendix order"
              data-testid={`proposal-att-move-down-${row.id}`}
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </span>
        )}
        {isChatSourced ? (
          <span
            className="shrink-0 text-[10px] text-[#647087] italic"
            title="Change inclusion (appendix vs inline) from the chat message's own controls."
          >
            {row.report_inclusion}
          </span>
        ) : (
        <select
          value={row.report_inclusion}
          disabled={saving}
          onChange={(e) => patch({ report_inclusion: e.target.value })}
          data-testid={`proposal-att-inclusion-${row.id}`}
          className="hv-input rounded px-1.5 py-0.5 text-[11px] cursor-pointer"
        >
          {opts.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        )}
        {saving && <Loader2 className="w-3 h-3 animate-spin text-cyan-300 shrink-0" />}
        {isChatSourced ? null : confirmDel ? (
          <span className="inline-flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => { onDelete(row.id); setConfirmDel(false); }}
              className="text-[10px] uppercase tracking-wide text-rose-300 hover:text-rose-200 px-1"
              data-testid={`proposal-att-delete-confirm-${row.id}`}
            >
              Delete?
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              className="text-[10px] text-[#647087] hover:text-white px-1"
              data-testid={`proposal-att-delete-cancel-${row.id}`}
            >
              cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            className="text-[#647087] hover:text-rose-300 shrink-0"
            aria-label="Delete attachment"
            data-testid={`proposal-att-delete-${row.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {row.report_inclusion === "inline" && (
        <input
          type="text"
          defaultValue={row.report_section || ""}
          placeholder="Section heading (optional — e.g. Site overview)"
          maxLength={80}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (row.report_section || "")) patch({ report_section: v });
          }}
          data-testid={`proposal-att-section-${row.id}`}
          className="hv-input rounded px-2 py-1 text-[11px] w-full"
        />
      )}
      <input
        type="text"
        defaultValue={row.caption || ""}
        placeholder="Caption (optional — shown under the image)"
        maxLength={400}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v !== (row.caption || "")) patch({ caption: v });
        }}
        data-testid={`proposal-att-caption-${row.id}`}
        className="hv-input rounded px-2 py-1 text-[11px] w-full"
      />
    </div>
  );
}

export default function ProposalAttachmentsUploader({ draftId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!draftId) return;
    try {
      const { data } = await api.get(
        `/workbench/drafts/${draftId}/report-attachments`,
      );
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      // Endpoint exists but tenant may have no rows yet — keep []
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        try {
          const meta = await uploadFile(f, { source: "workbench-proposal" });
          if (!meta?.id) continue;
          // PDFs default to appendix (spliced as-is). Images default to
          // appendix too — the rep flips to "Inline in body" if they
          // want it embedded.
          const { data } = await api.post(
            `/workbench/drafts/${draftId}/report-attachments`,
            {
              upload_id: meta.id,
              filename: meta.filename,
              content_type: meta.content_type,
              size_bytes: meta.size,
              report_inclusion: "appendix",
            },
          );
          setRows((prev) => [...prev, data]);
        } catch (e) {
          toast.error(
            e?.response?.data?.detail
            || `Couldn't attach ${f?.name || "file"}.`,
          );
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onPatch = async (aid, updates) => {
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${draftId}/report-attachments/${aid}`,
        updates,
      );
      setRows((prev) => prev.map((r) => (r.id === aid ? data : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update attachment.");
    }
  };

  const onDelete = async (aid) => {
    try {
      await api.delete(
        `/workbench/drafts/${draftId}/report-attachments/${aid}`,
      );
      setRows((prev) => prev.filter((r) => r.id !== aid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete attachment.");
    }
  };

  // Iter 427.p7 — Sort appendix rows by report_order (ascending);
  // inline rows tail after. This matches the PDF composer's sort key
  // so what you see is what prints.
  const sortedRows = [...rows].sort((a, b) => {
    const aa = a.report_inclusion === "appendix" ? 0 : 1;
    const bb = b.report_inclusion === "appendix" ? 0 : 1;
    if (aa !== bb) return aa - bb;
    return (a.report_order ?? 0) - (b.report_order ?? 0);
  });
  const appendixOrder = sortedRows
    .filter((r) => r.report_inclusion === "appendix")
    .map((r) => r.id);

  const reorderAppendix = async (newOrder) => {
    // Optimistic — update local state immediately, revert on failure.
    const snapshot = rows;
    const nextMap = Object.fromEntries(newOrder.map((id, i) => [id, i]));
    setRows((prev) => prev.map((r) => (
      r.report_inclusion === "appendix"
        ? { ...r, report_order: nextMap[r.id] ?? r.report_order }
        : r
    )));
    try {
      await api.post(
        `/workbench/drafts/${draftId}/report-attachments/reorder`,
        { ordered_ids: newOrder },
      );
    } catch (e) {
      setRows(snapshot);
      toast.error("Couldn't save the new order — try again.");
    }
  };

  const moveUp = (aid) => {
    const i = appendixOrder.indexOf(aid);
    if (i <= 0) return;
    const next = [...appendixOrder];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    reorderAppendix(next);
  };
  const moveDown = (aid) => {
    const i = appendixOrder.indexOf(aid);
    if (i < 0 || i >= appendixOrder.length - 1) return;
    const next = [...appendixOrder];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    reorderAppendix(next);
  };

  const counts = rows.reduce((acc, r) => {
    if (r.report_inclusion === "appendix") acc.appendix += 1;
    else if (r.report_inclusion === "inline") acc.inline += 1;
    return acc;
  }, { appendix: 0, inline: 0 });

  return (
    <div
      className="rounded-md border border-white/10 bg-white/[0.03] p-2.5 space-y-2"
      data-testid="proposal-attachments-uploader"
    >
      <div className="flex items-center gap-2">
        <FileStack className="w-3.5 h-3.5 text-cyan-300" />
        <span className="text-[11px] uppercase tracking-wide text-cyan-200/90">
          Proposal-only attachments · {rows.length}
        </span>
        <span className="text-[10px] text-[#647087]">
          {counts.appendix} appendix · {counts.inline} inline
        </span>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="hv-btn hv-btn-ghost text-[11px] ml-auto disabled:opacity-40"
          data-testid="proposal-att-upload-trigger"
          title="Attach a PDF or image that should appear in the proposal PDF but NOT in the chat history."
        >
          {uploading
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Plus className="w-3 h-3" />}
          {uploading ? "Uploading…" : "Add file"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="proposal-att-file-input"
        />
      </div>
      <p className="text-[10px] text-[#647087] leading-snug">
        Files attached here go straight into the final customer PDF (IQ
        Retail quotes, datasheets, drawings) WITHOUT cluttering the
        brainstorm chat. PDFs are spliced as-is at the back.
      </p>
      {loading ? (
        <div className="text-[11px] text-[#647087]">Loading attachments…</div>
      ) : rows.length === 0 ? (
        <div
          className="rounded border border-dashed border-white/15 px-3 py-3 text-center"
          data-testid="proposal-att-empty"
        >
          <p className="text-[11px] text-[#a0aabf]">
            No proposal attachments yet. Click <strong>Add file</strong> to upload an IQ Retail quote, datasheet or drawing.
          </p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="proposal-att-list">
          {sortedRows.map((r) => {
            const idxInAppendix = r.report_inclusion === "appendix"
              ? appendixOrder.indexOf(r.id) : -1;
            return (
              <AttachmentRow
                key={r.id}
                row={r}
                onPatch={onPatch}
                onDelete={onDelete}
                onMoveUp={moveUp}
                onMoveDown={moveDown}
                canMoveUp={idxInAppendix > 0}
                canMoveDown={idxInAppendix >= 0 && idxInAppendix < appendixOrder.length - 1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
